import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Paper,
  Button,
  Skeleton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { enhancedApi } from '../api';
import dayjs from 'dayjs';
import { DATE_PRESETS } from '../components/reports/reportDatePresets';

const fmtCurrency = (n) =>
  n != null && !isNaN(n)
    ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

const fmtPercent = (n) => (n != null && !isNaN(n) ? `${Number(n).toFixed(1)}%` : '—');

function SummaryCard({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'divider', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function presetToMonths(preset, customMonths) {
  switch (preset) {
    case 'this_month':
      return 1;
    case 'last_month':
      return 3;
    case 'quarter':
      return 3;
    case 'year':
      return 12;
    case 'custom':
      return customMonths;
    default:
      return 6;
  }
}

const RevenueForecast = () => {
  const theme = useTheme();
  const [forecastPreset, setForecastPreset] = useState('quarter');
  const [customMonths, setCustomMonths] = useState(6);
  const months = useMemo(
    () => presetToMonths(forecastPreset, customMonths),
    [forecastPreset, customMonths]
  );

  const [data, setData] = useState(null);
  const [ytdActual, setYtdActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchForecast = useCallback(() => {
    setLoading(true);
    setError(null);
    const m = Math.min(12, Math.max(1, months));
    enhancedApi
      .getRevenueForecast(m)
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load forecast'))
      .finally(() => setLoading(false));
  }, [months]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  useEffect(() => {
    const from = dayjs().startOf('year');
    const to = dayjs();
    enhancedApi
      .getProfitabilityReport({
        date_from: from.format('YYYY-MM-DD'),
        date_to: to.format('YYYY-MM-DD'),
        group_by: 'client',
      })
      .then((res) => {
        const items = res.data?.results ?? res.data?.rows ?? [];
        const sum = items.reduce((s, r) => s + (r.revenue ?? 0), 0);
        setYtdActual(sum);
      })
      .catch(() => setYtdActual(null));
  }, []);

  const monthlyData = useMemo(() => data?.forecast ?? data?.monthly_data ?? data?.months ?? [], [data]);

  const monthsElapsed = dayjs().month() + 1;
  const avgMonthlyActual = ytdActual != null && monthsElapsed > 0 ? ytdActual / monthsElapsed : null;

  const chartData = useMemo(() => {
    return monthlyData.map((m) => ({
      label: m.month_label ?? m.month ?? m.label ?? '—',
      projected: m.total_forecast ?? m.total ?? 0,
      actual: avgMonthlyActual ?? 0,
    }));
  }, [monthlyData, avgMonthlyActual]);

  const projectedThisMonth =
    data != null
      ? Number((data.monthly_recurring ?? 0) + (data.avg_monthly_adhoc ?? 0))
      : null;

  const growthPct =
    avgMonthlyActual && avgMonthlyActual > 0 && projectedThisMonth != null
      ? ((projectedThisMonth - avgMonthlyActual) / avgMonthlyActual) * 100
      : null;

  const empty = !loading && !error && monthlyData.length === 0;

  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;

  const handleExportCsv = () => {
    const header = 'Month,Projected,Actual YTD avg / mo';
    const body = monthlyData
      .map((m) => {
        const projected = m.total_forecast ?? m.total ?? 0;
        const label = m.month_label ?? m.month ?? m.label ?? '—';
        return [label, projected, avgMonthlyActual ?? ''].join(',');
      })
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-forecast-${months}mo.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const rangeHint =
    forecastPreset === 'custom'
      ? `Showing ${months} month${months === 1 ? '' : 's'} ahead (custom horizon).`
      : `Forward forecast horizon: ${months} month${months === 1 ? '' : 's'}.`;

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1">
            Revenue Forecast
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Projected monthly revenue versus your year-to-date average monthly actuals.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportCsv} disabled={loading || empty}>
          Export CSV
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <ToggleButtonGroup
          value={forecastPreset}
          exclusive
          size="small"
          onChange={(_, v) => v != null && setForecastPreset(v)}
          sx={{ flexWrap: 'wrap' }}
        >
          {DATE_PRESETS.map((p) => (
            <ToggleButton key={p.id} value={p.id} sx={{ textTransform: 'none' }}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {forecastPreset === 'custom' && (
          <TextField
            size="small"
            type="number"
            label="Months ahead"
            value={customMonths}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!isNaN(n)) setCustomMonths(Math.min(12, Math.max(1, n)));
            }}
            inputProps={{ min: 1, max: 12 }}
            sx={{ width: 140 }}
          />
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={340} sx={{ borderRadius: 2, mb: 3 }} />
        </>
      ) : empty ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderColor: 'divider',
            borderStyle: 'dashed',
          }}
        >
          <BarChartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            No data for selected period
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rangeHint} Add recurring invoices or paid history to populate the forecast.
          </Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            <SummaryCard label="Projected This Month" value={fmtCurrency(projectedThisMonth)} />
            <SummaryCard label="Actual YTD" value={fmtCurrency(ytdActual)} />
            <SummaryCard label="Growth %" value={growthPct != null ? fmtPercent(growthPct) : '—'} />
          </Box>

          {chartData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Projected vs actual run rate
              </Typography>
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                    <Tooltip formatter={(value) => fmtCurrency(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="projected"
                      name="Projected"
                      stroke={primaryColor}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      name="Actual (YTD avg / mo)"
                      stroke={secondaryColor}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default RevenueForecast;
